import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { ItemControls } from './ItemControls'
import { useBotArgFetch } from '@/hooks/use-bot-arg-fetch'
import { useAzureTts } from '@/contexts/AzureTtsProvider'
import { useTurns } from '@/contexts/TurnsProvider'
import { SpeakingRole, toMs, TurnStatus, TurnType, WinType } from '@db8/shared'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useHall } from '@/contexts/HallProvider'
import { useMeeting } from '@videosdk.live/react-sdk'
import { toast } from 'react-toastify'
import { useAssemblyLiveTranscription } from '@/hooks/use-assembly-live-transcription'
import { useUser } from '@clerk/clerk-react'
import { useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { sideBarModes } from '@/utils/common'
import { botMessageMap, userMessageMap, botErrorMap } from '@/constants/turnMessages'

export const UserTurn = ({ role, duration, userId, step }) => {
  const [ttsGenerating, setTtsGenerating] = useState(false)
  const [flowNotesGenerating, setFlowNotesGenerating] = useState(false)
  const [turnStarted, setTurnStarted] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [localMessage, setLocalMessage] = useState('')
  const justCancelled = useRef(false)
  const lastGeneratedStep = useRef<number | null>(null)
  const { participants } = useMeeting()
  const { getUser, currentUser } = useHallUsers()
  const { resetTimer, currentTurn, setMessage, status, setError, error, message, timer } =
    useTurns()
  const { user: clerkUser } = useUser()
  const { replaceTranscript, deleteTranscript, setHallMetadata, lastSender, hallMetadata, hall } =
    useHall()
  const botArgFetch = useBotArgFetch()
  const { playerReady, botArgument, setBotArgument, generateAudio, restartReading, cancelTts, playReading, pauseReading } =
    useAzureTts()
  const { fetchFlowNote } = useFlowNotes()
  const { startUserTranscription, stopUserTranscription } = useAssemblyLiveTranscription()
  const { setSideBarMode } = useMeetingAppContext()

  const messageMapping = role === 'bot' ? botMessageMap : userMessageMap
  const errorMapping =
    role === 'bot' ? botErrorMap : { TRANSCRIPTION_FAILED: 'Transcription failed.' }

  const user = useMemo(() => getUser(userId), [userId])
  const botId = user?.botId
  const speakingRole = user?.speakingRole

  const handleGenerateTTS = useCallback(
    async (argument: string) => {
      try {
        if (!argument) {
          setError('')
          return
        }
        setLocalMessage('Generating and uploading audio')
        await generateAudio(argument, botId)
        justCancelled.current = false
        setLocalMessage('Updating hall data…')
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          // no-op
        } else {
          // no-op
          setError('TTS_FAILED')
        }
      }
    },
    [botId, generateAudio, setError, currentUser?.displayName],
  )

  const handleFetchArgument = useCallback(
    async (botTeam: SpeakingRole, triggerName: string) => {
      try {
        setError('')
        setGenerating(true)
        setTtsGenerating(false)
        setHallMetadata({ status: TurnStatus.THINKING })
        setLocalMessage('Fetching AI argument')

        const argumentResponse = await botArgFetch.fetchAndPublishAiArg(
          botTeam,
          duration,
          currentTurn?.abbreviation,
          hall.format,
        )

        const ttsText = argumentResponse?.ttsText || ''
        const transcriptText = argumentResponse?.transcriptText || ''

        setLocalMessage('Updating hall data…')
        if (user) {
          replaceTranscript(currentTurn?.step, {
            text: transcriptText,
            hallUser: user,
            turnStep: currentTurn?.step,
            id: `transcript-${Date.now()}-${Math.random()}`,
            role: user.speakingRole,
          })
        }

        // Flow notes: generate immediately from the bot transcript (no need to wait for Next).
        try {
          await fetchFlowNote({ transcript: transcriptText, step: currentTurn?.step, turnType: currentTurn?.type })
        } catch {}

        setBotArgument(ttsText)
        setTtsGenerating(true)
        setLocalMessage('Generating and uploading audio')
        await handleGenerateTTS(ttsText)
        setLocalMessage('Updating hall data…')
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          // no-op
        } else {
          // no-op
          setError('FETCH_FAILED')
        }
      } finally {
        setGenerating(false)
        setTtsGenerating(false)
        if (!justCancelled.current) setHallMetadata({ status: TurnStatus.READY })
        setLocalMessage('')
      }
    },
    [
      currentTurn,
      duration,
      hall.format,
      user,
      botArgFetch,
      setError,
      setGenerating,
      setHallMetadata,
      replaceTranscript,
      setBotArgument,
      handleGenerateTTS,
    ],
  )

  useEffect(() => {
    if (role === 'bot') {
      if (generating) {
        if (flowNotesGenerating) setMessage('Generating Flow Notes')
        else if (ttsGenerating) setMessage('Generating TTS')
        else setMessage('Thinking')
      } else {
        setMessage(messageMapping[status])
      }
    } else {
      setMessage(messageMapping[status])
    }
    if (status === TurnStatus.CANCELLED) setMessage(messageMapping['CANCELLED'])
  }, [status, generating, role, ttsGenerating, flowNotesGenerating])

  useEffect(() => {
    if (role === 'bot' && !user) return
    if (role === 'bot') {

      if (
        currentTurn &&
        !justCancelled.current &&
        [TurnStatus.READY, TurnStatus.PAUSED].includes(hallMetadata?.status) &&
        (lastSender === clerkUser?.id || !lastSender) &&
        !generating &&
        currentTurn.step === step &&
        !botArgument &&
        !hallMetadata?.activeTts &&
        lastGeneratedStep.current !== currentTurn.step
      ) {
        lastGeneratedStep.current = currentTurn.step
        handleFetchArgument(speakingRole, '3')
      }
    }

    if (timer > 0 && !turnStarted) setTurnStarted(true)
  }, [
    currentTurn,
    role,
    hallMetadata?.status,
    hallMetadata?.activeTurn,
    hallMetadata?.turns,
    hallMetadata?.activeTts,
    generating,
    lastSender,
    clerkUser?.id,
    step,
    botArgument,
    timer,
    turnStarted,
    user?.speakingRole,
    playerReady,
  ])

  useEffect(() => {
    if (timer === 0 && turnStarted) onPause(true)
  }, [timer, turnStarted])

  useEffect(() => {
    if (error) setMessage(errorMapping[error])
  }, [error])

  const onRetry =
    role === 'bot'
      ? async () => {
          if (!botArgument && user && status !== TurnStatus.THINKING)
            handleFetchArgument(user.speakingRole, '1')
          else handleGenerateTTS(botArgument)
        }
      : undefined

  const onPause = useCallback(
    (turnComplete: boolean) => {
      if (turnComplete) {
        setHallMetadata({ status: TurnStatus.TURN_COMPLETE, timer })
        return
      }
      if (role === 'bot') {
        pauseReading()
        setHallMetadata({ status: TurnStatus.PAUSED, timer })
        return
      }
      // Human
      if (currentTurn?.type === TurnType.Preperatory) {
        setHallMetadata({ status: TurnStatus.PAUSED, timer })
        return
      }
      if (turnStarted) {
        stopUserTranscription()
        setHallMetadata({ status: TurnStatus.PAUSED, timer })
      }
    },
    [currentTurn?.type, role, setHallMetadata, timer, turnStarted, stopUserTranscription],
  )

  const onCancel = useCallback(() => {
    setError('')
    botArgFetch.cancelFetch()
    cancelTts()
    justCancelled.current = true
    setHallMetadata({ status: TurnStatus.CANCELLED })
    setGenerating(false)
    setTtsGenerating(false)
    setFlowNotesGenerating(false)
    lastGeneratedStep.current = null
  }, [setError, botArgFetch, cancelTts, setHallMetadata])

  const onPlay = useCallback(async () => {
    setTurnStarted(true)
    const userVideo = participants.get(user?.videoSDKId)
    if (role === 'bot') {
      // ensure playback starts
      playReading()
      setHallMetadata({ status: TurnStatus.PLAYING, timer })
    } else {
      if (!userVideo?.micOn && currentTurn?.type !== TurnType.Preperatory) {
        toast(`${user?.displayName} needs to turn on their mic.`, {
          position: 'bottom-left',
          autoClose: 3000,
          theme: 'light',
        })
        return
      }
      if (currentTurn?.type !== TurnType.Preperatory) {
        setHallMetadata({ status: TurnStatus.TURN_STARTING, timer })
        setTimeout(() => {
          startUserTranscription()
          setHallMetadata({ status: TurnStatus.RECORDING, timer })
        }, 3000)
      } else {
        setHallMetadata({ status: TurnStatus.PLAYING, timer })
      }
    }
  }, [
    participants,
    user?.videoSDKId,
    role,
    setHallMetadata,
    timer,
    currentTurn?.type,
    user?.displayName,
    startUserTranscription,
  ])

  const onNext = useCallback(async () => {

    if (role === 'bot') {
      setHallMetadata({ status: TurnStatus.THINKING })
      setGenerating(true)
      setTtsGenerating(false)
    }
    if (role === 'bot') {
      lastGeneratedStep.current = null
      setBotArgument('')
    }

    justCancelled.current = false
    const nextTurn = hallMetadata?.turns?.[hallMetadata?.activeTurn + 1]

    setFlowNotesGenerating(true)
    try {
      await fetchFlowNote()
    } finally {
      setFlowNotesGenerating(false)
    }

    // ⬇️ Only pin speakers for non-prep turns
    // For regular speech turns, only pin the primary speaker
    // For cross-examination turns, pin both speakers
    const shouldPinSecondary = nextTurn?.type === TurnType.AffirmativeCrossNegative ||
                               nextTurn?.type === TurnType.NegativeCrossAffirmative

    const speakers = nextTurn?.type === TurnType.Preperatory
      ? []
      : shouldPinSecondary
        ? [nextTurn?.primarySpeakerId, nextTurn?.secondarySpeakerId].filter(Boolean)
        : [nextTurn?.primarySpeakerId].filter(Boolean)

    const newMetadata: any = {
      activeTurn: (hallMetadata?.activeTurn ?? -1) + 1,
      activeTts: '',
      timer: nextTurn ? toMs(nextTurn?.duration) : 0,
      debateComplete: !nextTurn ? true : false,
      pinnedParticipants: speakers,
      status: TurnStatus.READY,
    }

    if (!nextTurn) {
      if (hall?.winType === WinType.AI) {
        setSideBarMode((s) => (s === sideBarModes.JUDGMENT ? null : sideBarModes.JUDGMENT))
        setHallMetadata({
          debateComplete: true,
          pollingStarted: true,
          ...newMetadata,
        })
      } else if (
        [WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL, WinType.BALLOT].includes(hall?.winType)
      ) {
        setHallMetadata({ pollingStarted: true, ...newMetadata })
      }
      if (hall?.winType !== WinType.AI) setSideBarMode(null)
    } else {
      setHallMetadata(newMetadata)
    }

    setGenerating(false)
  }, [
    role,
    hallMetadata?.activeTurn,
    hallMetadata?.turns,
    setHallMetadata,
    hall?.winType,
    setSideBarMode,
    fetchFlowNote,
    currentTurn,
    setBotArgument,
  ])

  const onRestart = useCallback(() => {
    resetTimer(duration)
    const ms = toMs(currentTurn?.duration)
    if (role === 'bot') {
      setGenerating(false)
      setTtsGenerating(false)
      restartReading()
      setHallMetadata({ status: TurnStatus.PLAYING, timer: ms, ttsTime: 0 })
    } else {
      setHallMetadata({ status: TurnStatus.PLAYING, timer: ms, ttsTime: 0 })
    }
  }, [resetTimer, duration, setHallMetadata, currentTurn?.duration, role, restartReading])

  const onRegenerate = useCallback(async () => {
    botArgFetch.cancelFetch()
    cancelTts()
    justCancelled.current = false
    lastGeneratedStep.current = null

    await deleteTranscript(currentTurn?.step, null)
    setBotArgument('')
    setError('')

    setGenerating(true)
    setTtsGenerating(false)
    setFlowNotesGenerating(false)
    setHallMetadata({
      status: TurnStatus.THINKING,
      timer: toMs(currentTurn?.duration),
      ttsTime: 0,
    })

    try {
      await handleFetchArgument(speakingRole, 'regen')
    } finally {
      setGenerating(false)
      setTtsGenerating(false)
    }
  }, [
    botArgFetch,
    cancelTts,
    deleteTranscript,
    currentTurn?.step,
    setBotArgument,
    setError,
    setHallMetadata,
    currentTurn?.duration,
    handleFetchArgument,
    speakingRole,
  ])

  const handlePauseFalse = useCallback(() => onPause(false), [onPause])

  return (
    <div className="mt-2">
      <ItemControls
        onPause={handlePauseFalse}
        onPlay={onPlay}
        onNext={onNext}
        onRestart={onRestart}
        onRetry={onRetry}
        userId={userId}
        isLoading={role === 'bot' && (status === TurnStatus.THINKING || generating)}
        isDisabled={
          (role === 'bot' && !(playerReady || Boolean(hallMetadata?.activeTts))) ||
          (timer === 0 && turnStarted)
        }
        message={localMessage || message}
        turnType={currentTurn?.type}
        onCancel={onCancel}
        error={error}
        regen={onRegenerate}
      />
    </div>
  )
}
